import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert } from '../atoms/Alert/Alert';
import { Heading } from '../atoms/Typography/Heading';
import { LikeButton } from './LikeButton';
import { CommentThread } from './CommentThread';
import {
  createEntryComment,
  deleteEntryComment,
  listEntryComments,
  setEntryLike,
  type CommunityEntry,
} from '../../services/communityService';
import { useTranslation } from '../../i18n/useTranslation';
import './JournalEntrySocial.css';

export const communityEntryQueryKey = (entryId: string) => ['communityEntry', entryId] as const;
export const entryCommentsQueryKey = (entryId: string) => ['entryComments', entryId] as const;
export const COMMUNITY_FEED_QUERY_KEY = ['communityFeed'] as const;

export interface JournalEntrySocialProps {
  entry: CommunityEntry;
  /** Heading level of the "Comments" title (default 2). */
  headingLevel?: 2 | 3;
}

/** Likes (toggle + count) and comments of one journal entry. */
export function JournalEntrySocial({ entry, headingLevel = 2 }: JournalEntrySocialProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [likeError, setLikeError] = useState(false);

  const likeMutation = useMutation({
    mutationFn: (liked: boolean) => setEntryLike(entry.id, liked),
    onSuccess: (state) => {
      setLikeError(false);
      queryClient.setQueryData<CommunityEntry>(communityEntryQueryKey(entry.id), (prev) =>
        prev ? { ...prev, userHasLiked: state.liked, likeCount: state.count } : prev,
      );
      void queryClient.invalidateQueries({ queryKey: COMMUNITY_FEED_QUERY_KEY });
    },
    onError: () => setLikeError(true),
  });

  return (
    <div className="rg-entry-social" data-testid="journal-entry-social">
      <div className="rg-entry-social__likes">
        <LikeButton
          liked={entry.userHasLiked}
          count={entry.likeCount}
          subject={entry.title}
          pending={likeMutation.isPending}
          onToggle={() => likeMutation.mutate(!entry.userHasLiked)}
        />
      </div>
      {likeError && (
        <Alert tone="danger" compact onDismiss={() => setLikeError(false)} dismissLabel={t('comments.dismiss')}>
          {t('community.likeFailed')}
        </Alert>
      )}
      <Heading level={headingLevel} variant="subsection">
        {t('community.commentsTitle', { count: entry.commentCount })}
      </Heading>
      <CommentThread
        testId="journal-entry-comments"
        queryKey={entryCommentsQueryKey(entry.id)}
        relatedQueryKeys={[communityEntryQueryKey(entry.id), COMMUNITY_FEED_QUERY_KEY]}
        load={() => listEntryComments(entry.id)}
        create={(body) => createEntryComment(entry.id, body)}
        remove={(commentId) => deleteEntryComment(entry.id, commentId)}
      />
    </div>
  );
}

export default JournalEntrySocial;
